import { showWarnNotification } from '@vrplatform/theme';
import { useCallback } from 'react';
import type { PlaidLinkOnEvent } from 'react-plaid-link';

type EmbeddedEvent =
  | 'AUTO_SUBMIT_PHONE'
  | 'BANK_INCOME_INSIGHTS_COMPLETED'
  | 'CLOSE_OAUTH'
  | 'CONNECT_NEW_INSTITUTION'
  | 'ERROR'
  | 'EXIT'
  | 'FAIL_OAUTH'
  | 'HANDOFF'
  | 'IDENTITY_MATCH_FAILED'
  | 'IDENTITY_MATCH_PASSED'
  | 'IDENTITY_VERIFICATION_START_STEP'
  | 'IDENTITY_VERIFICATION_PASS_STEP'
  | 'IDENTITY_VERIFICATION_FAIL_STEP'
  | 'IDENTITY_VERIFICATION_PENDING_REVIEW_STEP'
  | 'IDENTITY_VERIFICATION_CREATE_SESSION'
  | 'IDENTITY_VERIFICATION_RESUME_SESSION'
  | 'IDENTITY_VERIFICATION_PASS_SESSION'
  | 'IDENTITY_VERIFICATION_FAIL_SESSION'
  | 'IDENTITY_VERIFICATION_PENDING_REVIEW_SESSION'
  | 'IDENTITY_VERIFICATION_OPEN_UI'
  | 'IDENTITY_VERIFICATION_RESUME_UI'
  | 'IDENTITY_VERIFICATION_CLOSE_UI'
  | 'LAYER_AUTOFILL_NOT_AVAILABLE'
  | 'LAYER_NOT_AVAILABLE'
  | 'LAYER_READY'
  | 'MATCHED_SELECT_INSTITUTION'
  | 'OPEN'
  | 'OPEN_MY_PLAID'
  | 'OPEN_OAUTH'
  | 'SEARCH_INSTITUTION'
  | 'SELECT_AUTH_TYPE'
  | 'SELECT_BRAND'
  | 'SELECT_DEGRADED_INSTITUTION'
  | 'SELECT_DOWN_INSTITUTION'
  | 'SELECT_FILTERED_INSTITUTION'
  | 'SELECT_INSTITUTION'
  | 'SKIP_SUBMIT_PHONE'
  | 'SUBMIT_ACCOUNT_NUMBER'
  | 'SUBMIT_CREDENTIALS'
  | 'SUBMIT_DOCUMENTS'
  | 'SUBMIT_DOCUMENTS_ERROR'
  | 'SUBMIT_DOCUMENTS_SUCCESS'
  | 'SUBMIT_MFA'
  | 'SUBMIT_OTP'
  | 'SUBMIT_PHONE'
  | 'SUBMIT_ROUTING_NUMBER'
  | 'TRANSITION_VIEW'
  | 'VERIFY_PHONE'
  | 'VIEW_DATA_TYPES';

const eventMap: Record<
  EmbeddedEvent,
  | {
      title?: string;
      message: string;
    }
  | undefined
> = {
  AUTO_SUBMIT_PHONE: undefined,
  BANK_INCOME_INSIGHTS_COMPLETED: undefined,
  CLOSE_OAUTH: {
    message:
      'You closed the third-party website or mobile app without completing the authentication flow.',
  },
  CONNECT_NEW_INSTITUTION: {
    message:
      'You chose to link a new institution instead of linking a saved institution. This event is only emitted in the Link Returning User Experience flow.',
  },
  ERROR: undefined,
  EXIT: {
    message: 'You exited without completing the linking flow.',
  },
  FAIL_OAUTH: {
    message:
      'You closed the third-party website or mobile app without completing the authentication flow.',
  },
  HANDOFF: undefined,
  IDENTITY_MATCH_FAILED: {
    message:
      'An Identity Match check configured via the Account Verification Dashboard failed the Identity Match rules and did not detect a match.',
  },
  IDENTITY_MATCH_PASSED: undefined,
  IDENTITY_VERIFICATION_START_STEP: undefined,
  IDENTITY_VERIFICATION_PASS_STEP: undefined,
  IDENTITY_VERIFICATION_FAIL_STEP: undefined,
  IDENTITY_VERIFICATION_PENDING_REVIEW_STEP: undefined,
  IDENTITY_VERIFICATION_CREATE_SESSION: undefined,
  IDENTITY_VERIFICATION_RESUME_SESSION: undefined,
  IDENTITY_VERIFICATION_PASS_SESSION: undefined,
  IDENTITY_VERIFICATION_FAIL_SESSION: {
    message: 'We could not verify your identity.',
  },
  IDENTITY_VERIFICATION_PENDING_REVIEW_SESSION: undefined,
  IDENTITY_VERIFICATION_OPEN_UI: undefined,
  IDENTITY_VERIFICATION_RESUME_UI: undefined,
  IDENTITY_VERIFICATION_CLOSE_UI: undefined,
  LAYER_AUTOFILL_NOT_AVAILABLE: undefined,
  LAYER_NOT_AVAILABLE: undefined,
  LAYER_READY: undefined,
  MATCHED_SELECT_INSTITUTION: undefined,
  OPEN: undefined,
  OPEN_MY_PLAID: undefined,
  OPEN_OAUTH: undefined,
  SEARCH_INSTITUTION: undefined,
  SELECT_AUTH_TYPE: undefined,
  SELECT_BRAND: undefined,
  SELECT_DEGRADED_INSTITUTION: undefined,
  SELECT_DOWN_INSTITUTION: undefined,
  SELECT_FILTERED_INSTITUTION: undefined,
  SELECT_INSTITUTION: undefined,
  SKIP_SUBMIT_PHONE: undefined,
  SUBMIT_ACCOUNT_NUMBER: undefined,
  SUBMIT_CREDENTIALS: undefined,
  SUBMIT_DOCUMENTS: undefined,
  SUBMIT_DOCUMENTS_ERROR: {
    message:
      'We found an issue when submitting documents for an income verification flow.',
  },
  SUBMIT_DOCUMENTS_SUCCESS: undefined,
  SUBMIT_MFA: undefined,
  SUBMIT_OTP: undefined,
  SUBMIT_PHONE: undefined,
  SUBMIT_ROUTING_NUMBER: undefined,
  TRANSITION_VIEW: undefined,
  VERIFY_PHONE: undefined,
  VIEW_DATA_TYPES: undefined,
};

export function useNotifyOnPlaidEvent() {
  'use memo';

  return useCallback<PlaidLinkOnEvent>((e, metadata) => {
    const eventName = e as EmbeddedEvent;
    if (typeof eventName !== 'string') return;
    const event = eventMap[eventName];
    if (event) {
      showWarnNotification({
        title: event.title || 'Could not authenticate',
        message: event.message,
      });
    }
  }, []);
}
